/**
 * The R3F scene root. Composes the casino ROOM (carpet floor + wainscot walls),
 * the D-shape blackjack table, instanced cards/chips, blob characters, the 3D
 * backlit High Rollers sign, deterministic dice, lighting (HDRI + one shadow
 * light), and selective bloom. Everything reads from the Zustand store; nothing
 * here holds authoritative state.
 *
 * Draw-call budget (CLAUDE.md): room ~7, table ~7, cards 1 (instanced), chips 1
 * (instanced), sign ~8, dice ≤ contestants, blobs ~4/player + name label.
 * Comfortably under the 100–150 ceiling at 5–15 players.
 */

import { Suspense } from 'react';
import { OrbitControls } from '@react-three/drei';
import { Lighting } from './Lighting';
import { Room } from './Room';
import { Table } from './Table';
import { Cards } from './Cards';
import { Chips } from './Chips';
import { Blobs } from './Blobs';
import { Dice } from './Dice';
import { HighRollersSign } from './HighRollersSign';
import { Effects } from './Effects';
import { camera as cameraDefaults } from '../theme/tokens';

/**
 * Constrained mouse camera control. OrbitControls owns the look direction (its
 * `target` replaces the old manual lookAt) so the user can TILT UP to read the 3D
 * High Rollers sign on the back wall — which previously sat behind the DOM title —
 * and tilt back DOWN onto the felt. The polar/azimuth/distance clamps (theme/tokens
 * `camera.controls`) keep the camera in front of the curved player edge and stop it
 * flipping under or behind the table. Panning and screen-space panning are OFF so
 * the framing can't drift; only orbit + a clamped dolly are allowed.
 *
 * Pointer routing: the HUD container is `pointer-events:none` with `pointer-events:
 * auto` only on widgets, so drags on empty canvas area reach OrbitControls' listeners
 * on the canvas element; drags that start on a HUD widget do not.
 */
function CameraControls() {
  const [tx, ty, tz] = cameraDefaults.target;
  const c = cameraDefaults.controls;
  return (
    <OrbitControls
      makeDefault
      target={[tx, ty, tz]}
      enablePan={false}
      enableDamping
      dampingFactor={0.08}
      rotateSpeed={0.6}
      zoomSpeed={0.6}
      minPolarAngle={c.minPolarAngle}
      maxPolarAngle={c.maxPolarAngle}
      minAzimuthAngle={c.minAzimuthAngle}
      maxAzimuthAngle={c.maxAzimuthAngle}
      minDistance={c.minDistance}
      maxDistance={c.maxDistance}
    />
  );
}

export function Scene() {
  return (
    <>
      <CameraControls />
      <Lighting />

      {/* Guard any suspending children (textures/HDRI) behind a Suspense so a
          slow/missing asset never blanks the canvas. The guarded texture hooks
          themselves never suspend; this is belt-and-suspenders for drei helpers. */}
      <Suspense fallback={null}>
        <Room />
        <Table />
        <Cards />
        <Chips />
        <Blobs />
        <HighRollersSign />
        <Dice />
      </Suspense>

      <Effects />
    </>
  );
}
